import { ChangeEvent, FC, useEffect, useState } from 'react';

// Import material UI components
import Grid from '@mui/material/Grid';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import MainCard from 'ui-component/cards/MainCard';
import { useAgencyStore, usePolicyStore, useRegionStore } from 'Application/store';
import {
    exportToCSV,
    getAgencyNameFromStorage,
    getReportsFilterInputs,
    getReportsPageNumber,
    getReportsSearchInput,
    getUserRegionFromStorage,
    getUserRoleNameFromStorage,
    removeFilterInputs,
    removeReportsSearchInput,
    setReportsFilterInputs,
    setReportsPageNumber,
    setReportsSearchInput
} from 'Application/utils';

import Loader from 'ui-component/Loader';
import { Satellite, Search, DownloadRounded, FilterList, Close } from '@mui/icons-material';
import { AimsStatus, PolicyFlag, PolicyStatus, QuoteStatus, UserRole } from 'Application/models';
import { Box } from '@mui/system';
import CompletedTransactionsList from './quotes';
import { GeneratedQuotes } from '../Quotes';
import { getAgencyIdFromStorage } from 'Application/utils';
import {
    Divider,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Button,
    SelectChangeEvent,
    TextField,
    Drawer,
    IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'; // Import DatePicker from MUI or use a similar date library
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { BodyText, InitialsAvatar, TabButton, TitleText } from 'Application/components';
import dayjs from 'dayjs';
const getEnumFromString = (value: string): AimsStatus | undefined => {
    return AimsStatus[value as keyof typeof AimsStatus];
};

interface AllPoliciesProps {
    // agencyName: string;
}
interface GeneratedQuotesProps {
    agencyId: number;
}
export const AllPolicies: FC<AllPoliciesProps> = ({}) => {
    const storedPageNumber = getReportsPageNumber();
    const storedSearchInput = getReportsSearchInput();
    const storedFilterInputs = getReportsFilterInputs();

    const [page, setPage] = useState<number>(storedPageNumber === 0 ? 1 : storedPageNumber);
    const [pageSize, setPageSize] = useState<number>(10);

    // States for filter values (Temporary storage until applied)
    const [tempStartDate, setTempStartDate] = useState<string | null>(null);
    const [tempEndDate, setTempEndDate] = useState<string | null>(null);
    const [tempSearchInput, setTempSearchInput] = useState<string>(storedSearchInput ?? '');
    const [tempPolicyStatus, setTempPolicyStatus] = useState<string>(storedFilterInputs?.policyStatus ?? 'ALL');
    const [tempSelectedAgency, setTempSelectedAgency] = useState<string>(storedFilterInputs?.agency ?? '');
    const [tempCountrySearch, setTempCountrySearch] = useState<string>(storedFilterInputs?.country ?? 'All');

    // Applied filter states (Only update when "Apply Filters" is clicked)
    const [startDate, setStartDate] = useState<string | null>(null);
    const [endDate, setEndDate] = useState<string | null>(null);
    const [searchInput, setSearchInput] = useState<string>(storedSearchInput ?? '');
    const [policyStatus, setPolicyStatus] = useState<string>(storedFilterInputs?.policyStatus ?? 'ALL');
    const [selectedAgency, setSelectedAgency] = useState<string>(storedFilterInputs?.agency ?? '');
    const [countrySearch, setCountrySearch] = useState<string>(storedFilterInputs?.country ?? 'All');

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const userRole = getUserRoleNameFromStorage();
    const regionName = getUserRegionFromStorage();
    const transactionStatusOptions = ['ALL', ...Object.values(AimsStatus).map(String)];

    const {
        adminGetAllPolicy,
        adminGetAllPoliciesData,
        isLoading: isGetPoliciesLoading
    } = usePolicyStore((state) => ({
        adminGetAllPolicy: state.adminGetAllPolicy,
        adminGetAllPoliciesData: state.adminGetAllPoliciesData,
        isLoading: state.isLoading
    }));
    const {
        downloadAllPolicy,
        downloadAllPoliciesData,
        isLoading: isDownloadPoliciesLoading
    } = usePolicyStore((state) => ({
        downloadAllPolicy: state.downloadAllPolicy,
        downloadAllPoliciesData: state.downloadAllPoliciesData,
        isLoading: state.isLoading
    }));

    const {
        getAgenciesName,
        isLoading: isGetAgencyNameLoading,
        agencyNameData
    } = useAgencyStore((state) => ({
        getAgenciesName: state.getAgenciesName,
        isLoading: state.isLoading,
        agencyNameData: state.agencyNameData
    }));

    const { getRegions, regions, isLoading: isRegionsLoading } = useRegionStore();

    useEffect(() => {
        getAgenciesName();
        getRegions();
    }, []);

    const countryValues = regions && [{ name: 'All', regionID: 0 }, ...regions!];

    // Handles updating temporary filter values (no API call yet)
    const handleTempSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => setTempSearchInput(e.target.value);
    // const handleAgencyChange = (value: string) => setTempSelectedAgency(value);
    const handleCountrySearchChange = (value: string) => setTempCountrySearch(value);

    const handleAgencyChange = (value: string) => {
        console.log('Selected agency:', value); // Debugging
        setTempSelectedAgency(value);
    };
    const transactionStatusOption = getEnumFromString(policyStatus);

    const handlePolicyNumberSearch = async () => {
        setSearchInput(tempSearchInput);
        setReportsSearchInput(tempSearchInput);
        setPage(1);
    };

    // Called when "Clear Filters" button is clicked
    const clearFilters = () => {
        setTempSearchInput('');
        setTempStartDate(null);
        setTempEndDate(null);
        setTempPolicyStatus(transactionStatusOptions[0]); // Reset to default status
        setTempSelectedAgency('');
        setTempCountrySearch('All');

        setSearchInput('');
        setStartDate(null);
        setEndDate(null);
        setPolicyStatus(transactionStatusOptions[0]); // Reset to default status
        setSelectedAgency('');
        setCountrySearch('All');
        setPage(1);
        setReportsPageNumber(1);
        removeFilterInputs();
    };

    // Called when "Apply Filters" button is clicked
    const applyFilters = () => {
        setReportsFilterInputs({
            policyStatus: tempPolicyStatus,
            agency: tempSelectedAgency,
            country: tempCountrySearch
        });
        setPolicyStatus(tempPolicyStatus);
        setStartDate(tempStartDate);
        setEndDate(tempEndDate);
        setSelectedAgency(tempSelectedAgency);
        setCountrySearch(tempCountrySearch);
        setPage(1);
        setReportsPageNumber(1);
        setIsDrawerOpen(false); // Close filter drawer
    };

    const fetchPolicies = async () => {
        await adminGetAllPolicy(
            page,
            pageSize,
            PolicyFlag.ALL,
            getEnumFromString(policyStatus),
            searchInput,
            startDate,
            endDate,
            selectedAgency,
            countrySearch
        );
    };

    const downloadPolicies = async () => {
        await downloadAllPolicy(
            page,
            pageSize,
            PolicyFlag.ALL,
            getEnumFromString(policyStatus),
            searchInput,
            startDate,
            endDate,
            selectedAgency,
            regionName
        );
    };

    useEffect(() => {
        if (tempSearchInput === '') {
            setSearchInput(tempSearchInput);
            removeReportsSearchInput();
        }
    }, [tempSearchInput]);

    useEffect(() => {
        storedSearchInput && setReportsPageNumber(1);
        fetchPolicies();
        downloadPolicies();
    }, [page, searchInput, policyStatus, startDate, endDate, selectedAgency, countrySearch]);

    console.log(downloadAllPoliciesData, 'before');

    const handleDataExport = async () => {
        if (isDownloadPoliciesLoading) {
            console.log('Download in progress, please wait...');
            return;
        }

        console.log(downloadAllPoliciesData, 'after');

        const exportContent = (
            Array.isArray(downloadAllPoliciesData) ? downloadAllPoliciesData : (downloadAllPoliciesData?.content ?? [])
        ).map((policy) => {
            if (regionName === 'Kenya') {
                return {
                    'Policy Number': policy.policyNumber,
                    'Quotation Number': policy.quotationNumber,
                    'Issue date': policy.createdAt,
                    'Amount Paid (KSH)': policy.amount,
                    'Main customer': `${policy.customerName}`,
                    'Email address': policy.customerEmailAddress,
                    'Passport Number': policy.customerIdentificationNumber,
                    'Issuer Email Address': policy.issuerEmailAddress,
                    'Agency Name': policy.intermediaryName,
                    'Agency Email': policy.intermediaryEmailAddress,
                    'Aims Status': policy.policyTransactionStatus,
                    'Transaction Type': policy.transactionType,
                    'Transaction Number': policy.transactionNumber,
                    'Policy status': policy.policyStatus,
                    'Endorsement Number': policy.endorsementNumber
                };
            } else if (regionName === 'Rwanda') {
                return {
                    'Transaction Date': new Date(policy.transactionDate).toLocaleDateString('en-GB'),
                    'Transaction Type': policy.transactionType,
                    'Policy Number': policy.policyNumber,
                    'Endorsement Number': policy.endorsementNumber,
                    'Insured Party': policy.insuredParty,
                    'Business/Travel Type': policy.travelType,
                    'Customer Type': policy.customerType,
                    'Intermediary Type': policy.customerType,
                    'Intermediary Name': policy.intermediaryName,
                    'Debited By': policy.debitedBy,
                    'Coverage Period (From - To)': `${policy.departureDate} - ${policy.returnDate}`,
                    'Risk Sum Insured': policy.riskSumInsured,
                    Currency: policy.currency,
                    'Exchange Rate': policy.exchangeRate,
                    'Gross Amount': policy.grossAmount,
                    'Admin Fees': policy.adminFee,
                    'VAT Amount': policy.vat,
                    'Total Amount': policy.amount,
                    Description: policy.reasonOfTravel,
                    Status: policy.policyTransactionStatus
                };
            }
            return {};
        });

        if (exportContent.length === 0) {
            console.log('No data available to export.');
            return;
        }

        exportToCSV('Policies', exportContent);
    };

    return (
        <>
            <Box className="flex flex-wrap items-center justify-left gap-x-4 mb-4">
                {/* Policy Number */}
                <Box className="col-span-full sm:w-1/3.5">
                    <TextField
                        id="policyNumber"
                        label="Search"
                        helperText="Search by customer name, customer email or policy number"
                        variant="outlined"
                        value={tempSearchInput}
                        onChange={handleTempSearchInputChange}
                        onKeyDown={(e) => e.key === 'Enter' && handlePolicyNumberSearch()}
                        fullWidth
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search className="hover:cursor-pointer" onClick={handlePolicyNumberSearch} />
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>
                <Box className="flex flex-wrap gap-x-4 -mt-5">
                    {/* Button to Open Drawer */}
                    <Button variant="contained" startIcon={<FilterList />} onClick={() => setIsDrawerOpen(true)}>
                        Filter
                    </Button>

                    {!isGetPoliciesLoading && !isDownloadPoliciesLoading && (
                        <Button onClick={handleDataExport} endIcon={<DownloadRounded />} disableElevation variant="contained">
                            Export Data
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Drawer */}
            <Drawer anchor="right" open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
                <Box width={350} p={3} role="presentation">
                    {/* Header */}
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        <h2>Filters</h2>
                        <IconButton onClick={() => setIsDrawerOpen(false)}>
                            <Close />
                        </IconButton>
                    </Box>

                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Grid container spacing={2} mt={2}>
                            {/* Policy Status */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="policy-status-label">Policy Status</InputLabel>
                                    <Select
                                        labelId="policy-status-label"
                                        id="policy-status"
                                        label="Policy Status"
                                        variant="outlined"
                                        value={storedSearchInput ?? tempPolicyStatus}
                                        onChange={(e) => setTempPolicyStatus(e.target.value)}
                                    >
                                        {transactionStatusOptions.map((status) => (
                                            <MenuItem key={status} value={status}>
                                                {status}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Agency Name */}
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel id="agency-name-label">Intermediary Name</InputLabel>
                                    <Select
                                        labelId="agency-name-label"
                                        value={tempSelectedAgency} // Bind to tempSelectedAgency
                                        onChange={(e) => handleAgencyChange(e.target.value)}
                                        label="Agency Name"
                                    >
                                        {isGetAgencyNameLoading ? (
                                            <MenuItem disabled>Loading...</MenuItem>
                                        ) : (
                                            agencyNameData?.map((agency: { name: string }) => (
                                                <MenuItem key={agency.name} value={agency.name}>
                                                    {agency.name}
                                                </MenuItem>
                                            ))
                                        )}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Date Pickers */}
                            <Grid item xs={6}>
                                <DatePicker label="Start Date" value={startDate} onChange={(newValue) => setTempStartDate(newValue)} />
                            </Grid>
                            <Grid item xs={6}>
                                <DatePicker label="End Date" value={endDate} onChange={(newValue) => setTempEndDate(newValue)} />
                            </Grid>

                            {/* Country Filter */}
                            <Grid item xs={12}>
                                {userRole === UserRole.ROLE_ADMINISTRATOR && (
                                    <FormControl fullWidth>
                                        <InputLabel id="search-country-label">Search by country</InputLabel>
                                        <Select
                                            labelId="search-country-label"
                                            id="search-country"
                                            value={tempCountrySearch}
                                            fullWidth
                                            label="Search by country"
                                            variant="outlined"
                                            onChange={(e: SelectChangeEvent) => handleCountrySearchChange(e.target.value)}
                                        >
                                            {isRegionsLoading ? (
                                                <MenuItem disabled>Loading...</MenuItem>
                                            ) : (
                                                countryValues?.map((country) => {
                                                    return (
                                                        <MenuItem key={country.regionID} value={country.name}>
                                                            {country.name}
                                                        </MenuItem>
                                                    );
                                                })
                                            )}
                                        </Select>
                                    </FormControl>
                                )}
                            </Grid>
                        </Grid>
                    </LocalizationProvider>

                    {/* Buttons */}
                    <Box mt={3} display="flex" justifyContent="space-between">
                        <Button variant="outlined" color="secondary" onClick={clearFilters}>
                            Clear Filters
                        </Button>

                        <Button variant="contained" onClick={applyFilters}>
                            Apply Filters
                        </Button>
                    </Box>
                </Box>
            </Drawer>

            <Divider className="my-6" />

            {/* Show loader only when fetching policies */}
            <Box>
                {isGetPoliciesLoading || adminGetAllPoliciesData === null ? (
                    <Loader />
                ) : (
                    <>
                        <CompletedTransactionsList policies={adminGetAllPoliciesData} />
                        <Grid container justifyContent="space-between">
                            {adminGetAllPoliciesData.totalPages > 1 && (
                                <Pagination
                                    className="mt-6"
                                    count={adminGetAllPoliciesData?.totalPages}
                                    page={page}
                                    onChange={(event, value) => {
                                        setPage(value);
                                        setReportsPageNumber(value);
                                    }}
                                    color="primary"
                                />
                            )}
                        </Grid>
                    </>
                )}
            </Box>
        </>
    );
};

export const AdminCompletedTransactions = () => {
    const [activeTab, setActiveTab] = useState('AllPolicies');
    const toggleSelectedItem = (value: string) => setActiveTab(value);
    const agencyId: number = getAgencyIdFromStorage()!;

    return (
        <MainCard>
            <Box className="flex items-end space-x-6 mb-6">
                <InitialsAvatar initials={'R'} />
                <Box>
                    <TitleText text={'Reports'} />
                    <BodyText text={'View detailed reports on all completed transactions and generated quotes'} />
                </Box>
            </Box>
            <Box className="flex flex-wrap items-center space-x-2 mb-6">
                <TabButton isActive={activeTab === 'AllPolicies'} text={'Policies'} onClick={() => toggleSelectedItem('AllPolicies')} />
                <TabButton
                    isActive={activeTab === 'GeneratedQuotes'}
                    text={'Generated Quotes'}
                    onClick={() => toggleSelectedItem('GeneratedQuotes')}
                />
            </Box>
            {activeTab === 'AllPolicies' && <AllPolicies />}
            {activeTab === 'GeneratedQuotes' && <GeneratedQuotes agencyId={agencyId} allowAllFlag={true} />}
        </MainCard>
    );
};
